import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import "./VatandasNavbar.css";

import {
  Bars3Icon,
  XMarkIcon,
  HomeIcon,
  MapIcon,
  UserIcon,
  ChevronDownIcon,
  ChatBubbleLeftRightIcon,
  ClipboardDocumentListIcon,
  DocumentPlusIcon,
  MagnifyingGlassIcon,
} from "@heroicons/react/24/outline";

import kimLogo from "../assets/images/kim_logo.png";

const MOBILE_BP = 760;
const COMPACT_BP = 1200;
const SCROLL_TRIGGER = 80;

function useMedia(query, initial = false) {
  const [matches, setMatches] = useState(() => {
    if (typeof window === "undefined" || !window.matchMedia) return initial;
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mql = window.matchMedia(query);
    const onChange = () => setMatches(mql.matches);

    if (mql.addEventListener) {
      mql.addEventListener("change", onChange);
      return () => mql.removeEventListener("change", onChange);
    }
    mql.addListener(onChange);
    return () => mql.removeListener(onChange);
  }, [query]);

  return matches;
}

const VatandasNavbar = () => {
  const location = useLocation();

  const isMobile = useMedia(`(max-width: ${MOBILE_BP}px)`, false);
  const isCompact = useMedia(`(max-width: ${COMPACT_BP}px)`, false);

  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [opsOpen, setOpsOpen] = useState(false);

  const opsRef = useRef(null);

  // Logo boyutu — cep/taşma etkisini koruyan mantık
  const navbarHeight = isMobile ? 80 : 100;
  const logoTop = 0;
  const logoSize = 1 * (navbarHeight - logoTop);

  // Mobilde okunabilirlik için: her zaman “scrolled” görünümü
  const effectiveScrolled = isMobile ? true : isScrolled;

  const isOpsActive = useMemo(() => {
    const p = location.pathname;
    return p === "/basvuru-yap" || p === "/basvuru-takip" || p === "/memnuniyet-anketi";
  }, [location.pathname]);

  // Başlık: sadece biraz küçült
  const titleText = "Arsuz Çözüm Merkezi";
  const titleStyle = useMemo(() => {
    // Desktop'ta çok az küçült, mobilde daha dengeli küçült
    return {
      fontSize: isMobile ? 22 : 30,
      lineHeight: 1.08,
    };
  }, [isMobile]);

  // Scroll state (desktop için anlamlı)
  useEffect(() => {
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(() => {
        setIsScrolled(window.scrollY >= SCROLL_TRIGGER);
        ticking = false;
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Route değişince menüleri kapat
  useEffect(() => {
    setOpsOpen(false);
    setMobileOpen(false);
  }, [location.pathname]);

  // Mobil menü açıkken body scroll kilidi (mobil davranışı düzeltir)
  useEffect(() => {
    if (!mobileOpen) return;

    const prevOverflow = document.body.style.overflow;
    const prevTouch = document.body.style.touchAction;

    document.body.style.overflow = "hidden";
    document.body.style.touchAction = "none";

    return () => {
      document.body.style.overflow = prevOverflow;
      document.body.style.touchAction = prevTouch;
    };
  }, [mobileOpen]);

  // Dropdown dış tık + ESC
  useEffect(() => {
    if (!opsOpen) return;

    const onPointerDown = (e) => {
      const el = opsRef.current;
      if (!el) return;
      if (!el.contains(e.target)) setOpsOpen(false);
    };

    const onKeyDown = (e) => {
      if (e.key === "Escape") setOpsOpen(false);
    };

    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("touchstart", onPointerDown, { passive: true });
    document.addEventListener("keydown", onKeyDown);

    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("touchstart", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [opsOpen]);

  const closeAll = () => {
    setOpsOpen(false);
    setMobileOpen(false);
  };

  const navClass = [
    "vnav",
    isMobile ? "is-mobile" : "",
    effectiveScrolled ? "is-scrolled" : "is-top",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <>
      <header className={navClass}>
        <div className="vnav__inner">
          {/* Brand (logo pocket korunur) */}
          <Link to="/" className="vnav__brand" onClick={closeAll} aria-label="Anasayfa">
            <div className="vnav__logoWrap" style={{ ["--logoSize"]: `${logoSize}px` }}>
              <div className="vnav__logoPocket" aria-hidden="true" />
              <img className="vnav__logoImg" src={kimLogo} alt="Arsuz Belediyesi" />
            </div>

            <div className="vnav__brandText">
              {/* Yazı küçültme: sadece JS ile (inline) */}
              <div className="vnav__title" style={titleStyle}>
                {titleText}
              </div>
            </div>
          </Link>

          {/* Desktop nav */}
          <nav className="vnav__nav" aria-label="Vatandaş menüsü">
            <Link
              to="/"
              className={`vnav__item ${location.pathname === "/" ? "is-active" : ""}`}
              title="Anasayfa"
            >
              <span className="vnav__ico" aria-hidden="true">
                <HomeIcon />
              </span>
              {!isCompact ? <span className="vnav__label">Anasayfa</span> : null}
            </Link>

            {/* İşlemler dropdown */}
            <div className="vnav__itemGroup" ref={opsRef}>
              <button
                type="button"
                className={`vnav__item vnav__itemBtn ${isOpsActive ? "is-active" : ""}`}
                onClick={() => setOpsOpen((p) => !p)}
                aria-haspopup="menu"
                aria-expanded={opsOpen ? "true" : "false"}
                title="İşlemler"
              >
                <span className="vnav__ico" aria-hidden="true">
                  <ChatBubbleLeftRightIcon />
                </span>
                {!isCompact ? <span className="vnav__label">İşlemler</span> : null}
                <span className={`vnav__chev ${opsOpen ? "is-open" : ""}`} aria-hidden="true">
                  <ChevronDownIcon />
                </span>
              </button>

              <div className={`vnav__dropdown ${opsOpen ? "is-open" : ""}`} role="menu">
                <Link
                  to="/basvuru-yap"
                  className={`vnav__dropItem ${location.pathname === "/basvuru-yap" ? "is-active" : ""}`}
                  role="menuitem"
                  onClick={() => setOpsOpen(false)}
                >
                  <span className="vnav__dropIco" aria-hidden="true">
                    <DocumentPlusIcon />
                  </span>
                  <span>Başvuru Yap</span>
                </Link>

                <Link
                  to="/basvuru-takip"
                  className={`vnav__dropItem ${location.pathname === "/basvuru-takip" ? "is-active" : ""}`}
                  role="menuitem"
                  onClick={() => setOpsOpen(false)}
                >
                  <span className="vnav__dropIco" aria-hidden="true">
                    <MagnifyingGlassIcon />
                  </span>
                  <span>Başvuru Takip</span>
                </Link>

                <Link
                  to="/memnuniyet-anketi"
                  className={`vnav__dropItem ${
                    location.pathname === "/memnuniyet-anketi" ? "is-active" : ""
                  }`}
                  role="menuitem"
                  onClick={() => setOpsOpen(false)}
                >
                  <span className="vnav__dropIco" aria-hidden="true">
                    <ClipboardDocumentListIcon />
                  </span>
                  <span>Memnuniyet Anketi</span>
                </Link>
              </div>
            </div>

            <Link
              to="/kent-haritasi"
              className={`vnav__item ${location.pathname === "/kent-haritasi" ? "is-active" : ""}`}
              title="Kent Haritası"
            >
              <span className="vnav__ico" aria-hidden="true">
                <MapIcon />
              </span>
              {!isCompact ? <span className="vnav__label">Kent Haritası</span> : null}
            </Link>

            <Link
              to="/giris"
              className={`vnav__item ${location.pathname === "/giris" ? "is-active" : ""}`}
              title="Giriş Yap"
            >
              <span className="vnav__ico" aria-hidden="true">
                <UserIcon />
              </span>
              {!isCompact ? <span className="vnav__label">Giriş Yap</span> : null}
            </Link>
          </nav>

          {/* Mobile toggle */}
          <button
            type="button"
            className="vnav__burger"
            onClick={() => setMobileOpen((p) => !p)}
            aria-label={mobileOpen ? "Menüyü kapat" : "Menüyü aç"}
          >
            {mobileOpen ? <XMarkIcon /> : <Bars3Icon />}
          </button>
        </div>
      </header>

      {/* Mobile drawer */}
      <div className={`vnavDrawer ${mobileOpen ? "is-open" : ""}`} role="dialog" aria-modal="true">
        <div className="vnavDrawer__head">
          {/* İSTEK: Menü açılınca logo + isim görünsün */}
          <div
            className="vnavDrawer__brand"
            style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}
          >
            <img
              src={kimLogo}
              alt="Arsuz Belediyesi"
              style={{
                width: 34,
                height: 34,
                borderRadius: 999,
                background: "rgba(255,255,255,0.12)",
                padding: 4,
                flex: "0 0 auto",
              }}
            />
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 800, letterSpacing: "-0.02em", fontSize: 14, lineHeight: 1.1 }}>
                {titleText}
              </div>
            </div>
          </div>

          <button className="vnavDrawer__close" onClick={closeAll} aria-label="Kapat" type="button">
            <XMarkIcon />
          </button>
        </div>

        <div className="vnavDrawer__body">
          <Link
            to="/"
            className={`vnavDrawer__link ${location.pathname === "/" ? "is-active" : ""}`}
            onClick={closeAll}
          >
            <HomeIcon />
            <span>Anasayfa</span>
          </Link>

          <div className="vnavDrawer__section">
            <div className="vnavDrawer__sectionTitle">
              <ChatBubbleLeftRightIcon />
              <span>İşlemler</span>
            </div>

            <Link
              to="/basvuru-yap"
              className={`vnavDrawer__sublink ${location.pathname === "/basvuru-yap" ? "is-active" : ""}`}
              onClick={closeAll}
            >
              <DocumentPlusIcon />
              <span>Başvuru Yap</span>
            </Link>

            <Link
              to="/basvuru-takip"
              className={`vnavDrawer__sublink ${location.pathname === "/basvuru-takip" ? "is-active" : ""}`}
              onClick={closeAll}
            >
              <MagnifyingGlassIcon />
              <span>Başvuru Takip</span>
            </Link>

            <Link
              to="/memnuniyet-anketi"
              className={`vnavDrawer__sublink ${
                location.pathname === "/memnuniyet-anketi" ? "is-active" : ""
              }`}
              onClick={closeAll}
            >
              <ClipboardDocumentListIcon />
              <span>Memnuniyet Anketi</span>
            </Link>
          </div>

          <Link
            to="/kent-haritasi"
            className={`vnavDrawer__link ${location.pathname === "/kent-haritasi" ? "is-active" : ""}`}
            onClick={closeAll}
          >
            <MapIcon />
            <span>Kent Haritası</span>
          </Link>

          <Link
            to="/giris"
            className={`vnavDrawer__link ${location.pathname === "/giris" ? "is-active" : ""}`}
            onClick={closeAll}
          >
            <UserIcon />
            <span>Giriş Yap</span>
          </Link>
        </div>
      </div>

      {/* Backdrop */}
      <button
        type="button"
        className={`vnavBackdrop ${mobileOpen ? "is-open" : ""}`}
        onClick={closeAll}
        aria-label="Menüyü kapat"
      />
    </>
  );
};

export default VatandasNavbar;
